
import { Options, prop, Vue } from 'vue-class-component';
import html2canvas from 'html2canvas';
import JSPDF from "jspdf";
import { MainAppController } from '@/app/ui/controllers/MainAppController';
import { InvoiceDetail, SttInvoice } from '@/domain/entities/PrintReceipt';
import formatPrice from '@/app/infrastructures/misc/common-library/v2/FormatPrice';
import formatDateBasedTimezoneValue from '@/app/infrastructures/misc/common-library/v2/FormatDateBasedTimezoneValue';
import { AccountController } from '@/app/ui/controllers/AccountController';

class Props {
  invoiceData = prop<InvoiceDetail>({
    default: () => new InvoiceDetail(),
    type: Object
  });
  items = prop<SttInvoice[]>({
    default: () => [],
    type: Object
  });
}

@Options({})
class InvoicePDFA4 extends Vue.with(Props) {
  get accountCurrency() {
    return (
      (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ?? "IDR"
    );
  }
  formatDateNormal(date: any, format: string) {
    return formatDateBasedTimezoneValue(date, format);
  }
  formatPriceAmount(amount: number) {
    return formatPrice(amount, "", this.accountCurrency as "IDR" | "MYR");
  }
  show = false;

  get countryCode() {
    return AccountController.accountData.countryCode || "ID";
  }

  get chunkedPages() {
    const pages: SttInvoice[][][] = [];
    let currentIndex = 0;

    if (currentIndex < this.items.length) {
      const page1: SttInvoice[][] = [];

      const chunk1 = this.items.slice(currentIndex, currentIndex + 17);
      if (chunk1.length > 0) {
        page1.push(chunk1)
        currentIndex += chunk1.length;
      }

      const chunk2 = this.items.slice(currentIndex, currentIndex + 17);
      if (chunk2.length > 0) {
        page1.push(chunk2);
        currentIndex += chunk2.length
      }

      if (page1.length > 0) {
        pages.push(page1)
      };
    }

    while (currentIndex < this.items.length) {
      const page = [];

      const chunk1 = this.items.slice(currentIndex, currentIndex + 20);
      page.push(chunk1)
      currentIndex += chunk1.length;

      const chunk2 = this.items.slice(currentIndex, currentIndex + 20);
      page.push(chunk2);
      currentIndex += chunk2.length

      if (page.length > 0) {
        pages.push(page)
      };
    }

    if (!pages.length) {
      return [
        [
          [
            new SttInvoice({sttNo: "-"})
          ]
        ]
      ]
    }

    return pages;
  }

  async generatePdfA4() {
    const pdf = new JSPDF("l", "mm", "a4", true);
    const pdfWidth = pdf.internal.pageSize.getWidth(); // 297mm
    const pdfHeight = pdf.internal.pageSize.getHeight(); // 210mm

    for (const [index] of this.chunkedPages.entries()) {
      const canvas = await this.generatePngA4(`invoice-pdf-a4-${index}`, false) as HTMLCanvasElement
      const imgData = canvas.toDataURL();

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      if (this.chunkedPages.length !== 1 && index !== this.chunkedPages.length - 1) pdf.addPage()
    }

    const blob = pdf.output("blob");

    return blob;
  }

  async downloadPdfA4() {
    const blob = await this.generatePdfA4();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl);
  }

  async generatePngA4(elementId = "invoice-png-a4", returnBlob = true) {
    this.show = true;
    MainAppController.showLoading();

    await this.$nextTick();

    const element = document.getElementById(elementId)!;

    const originalDisplay = element.style.display;

    element.style.display = "block";
    element.style.position = "absolute";
    element.style.left = "-99999px"

    const style = document.createElement('style');
    document.head.appendChild(style);

    style.sheet?.insertRule('body > div:last-child img { display: inline-block; }');

    const canvas = await html2canvas(element, {scale: 2, width: 842});
    style.remove();
    element.style.display = originalDisplay;
    element.style.position = '';
    element.style.left = '';

    const imgBlob = await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "png", 1)
    })

    MainAppController.closeLoading();
    this.show = false;
    if (returnBlob) {
      return imgBlob as Blob;
    } else {
      return canvas;
    }
  }

  async downloadPngA4() {
    const imgBlob = await this.generatePngA4() as Blob;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(imgBlob)
    link.download = `Invoice-${this.invoiceData.invoiceNo}.png`;
    link.click();
    link.remove();
  }
}

export default InvoicePDFA4;
